// ===========================================
// Effective view resolution for AdminLayout
// ===========================================
// Which navigation a user sees is driven by WHERE they are (the URL's first
// segment after the school slug), falling back to their last explicit view
// switch for paths that do not encode one (e.g. /student).
//
// Pure and exported so the decision can be pinned by a unit test without
// rendering the 1,100-line layout. Bug it exists to prevent (2026-09-16):
// `/admin` used to map to 'admin' unconditionally, so a PARENT or TEACHER
// holding a custom staff role reached the admin dashboard but got the
// role-based fallback menu — the PARENT/TEACHER sidebar — and switching to
// Staff View visibly did nothing.

import type { AdminTeacherView } from '../../contexts/AdminTeacherViewContext.types';
import type { User } from '../../contexts/AuthContext.types';
import type { PermissionAction, PermissionResource } from '../../types/permissions.types';

/** The subset of the auth user the decision depends on. */
export type EffectiveViewUser = Pick<User, 'role' | 'isStaff' | 'isSuperAdmin'> | null | undefined;

export function resolveEffectiveView(
  pathname: string,
  currentView: AdminTeacherView,
  user: EffectiveViewUser
): AdminTeacherView {
  const pathParts = pathname.split('/');
  // pathParts: ['', schoolSlug, section, ...rest]
  const section = pathParts[2];

  if (section === 'admin') {
    // /admin hosts BOTH the full admin view and the permission-filtered staff
    // view. Staff who are not ADMINs get the staff view; ADMINs keep the full
    // config. `isSuperAdmin` is never read (Full access is retired).
    const isNonAdminStaff = user?.isStaff === true && user.role !== 'ADMIN';
    return isNonAdminStaff ? 'staff' : 'admin';
  }
  if (section === 'teacher') return 'teacher';
  if (section === 'parent') return 'parent';

  // For paths that don't encode the view (e.g. /student), fall back to currentView
  return currentView;
}

/**
 * The profile menu's view switcher: "Viewing as" is the view of the page the user is ON
 * (`routeView`, from `resolveEffectiveView`), and "Switch to" offers every OTHER available view.
 * Never the stored view: a staff teacher lands on /teacher after login while the stored view may
 * still say Staff, and the menu then offered "Switch to Teacher View" for the page they were
 * already on (2026-10-11).
 */
export function viewSwitcherOptions(
  routeView: AdminTeacherView,
  availableViews: readonly AdminTeacherView[]
): { current: AdminTeacherView; switchTo: AdminTeacherView[] } {
  return { current: routeView, switchTo: availableViews.filter((v) => v !== routeView) };
}

export type BottomNavRole = 'ADMIN' | 'TEACHER' | 'PARENT' | 'STUDENT';

/**
 * The phone's bottom navigation follows the view the user is ON, like the sidebar (roles 1b §5.15):
 * a staff member on /admin gets the office items (`filtered: true`, by her permissions), and an
 * ADMIN on /teacher gets the teacher items. A view the login cannot hold falls back to its role,
 * so a stored view never hands a plain teacher the admin items.
 */
export function bottomNavFor(
  routeView: AdminTeacherView,
  user: Pick<User, 'role' | 'isStaff'>
): { role: BottomNavRole; filtered: boolean } {
  const role = user.role as BottomNavRole;
  if (role === 'STUDENT') return { role, filtered: false };
  if (routeView === 'teacher') return { role: 'TEACHER', filtered: false };
  if (routeView === 'parent') return { role: 'PARENT', filtered: false };
  if (routeView === 'staff' && user.isStaff && role !== 'ADMIN') return { role: 'ADMIN', filtered: true };
  if (routeView === 'admin' && role === 'ADMIN') return { role: 'ADMIN', filtered: false };
  return { role, filtered: false };
}

/** The view segment of an app path: `/{slug}/{view}/…` → `view`. Never a substring match. */
export function pathViewSection(pathname: string): string | undefined {
  return pathname.split('/')[2];
}

/**
 * True on the TEACHER pages (`/{slug}/teacher/…`). The one test for "is this the teacher view":
 * `pathname.includes('/teacher')` also matched a school slug starting with "teacher" (whole-slice
 * /qa F-L2), which sent `mine` from an owner's Classes page and hid the calendar's edit controls.
 */
export function isTeacherViewPath(pathname: string): boolean {
  return pathViewSection(pathname) === 'teacher';
}

/**
 * A parent's stored "student view" applies on the PARENT pages only. The flag outlives logout, and a
 * PARENT login holding a staff role lands on /admin (roles 1b), where a stale flag hid the whole
 * navigation and skipped the admin route guard (whole-slice /qa F-M1).
 */
export function studentViewActive(pathname: string, storedStudentView: boolean): boolean {
  return storedStudentView && pathViewSection(pathname) === 'parent';
}

export interface LayoutReadGates {
  meetAndGreetCounts: boolean;
  onboardingCounts: boolean;
  onboardingConfig: boolean;
  navigation: boolean;
}

/**
 * Which of AdminLayout's admin reads may run (#272): only on the admin pages (their data feeds the
 * admin and staff sidebars, which exist only there), and only what the caller may read. Before
 * this, every teacher, parent and student page load requested all four and got four 403s.
 *
 * Path-based on purpose, never `resolveEffectiveView`: on /student that falls back to the stored
 * view, which is 'admin' for a STUDENT. Not a security boundary; the server gates still refuse.
 */
export function layoutReadGates(
  pathname: string,
  can: (resource: PermissionResource, action: PermissionAction) => boolean
): LayoutReadGates {
  const onAdminPages = pathViewSection(pathname) === 'admin';
  return {
    meetAndGreetCounts: onAdminPages && can('meet_and_greet', 'read'),
    onboardingCounts: onAdminPages && can('meet_and_greet', 'read'),
    onboardingConfig: onAdminPages && can('system_settings', 'read'),
    navigation: onAdminPages && can('system_settings', 'read'),
  };
}
